import React, { useMemo, useState } from 'react';
import { 
  Trash2, 
  Star,
  AlertCircle,
  Package,
  User,
  DollarSign,
  CheckCircle,
  XCircle,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect, StatsGrid, PillToggle } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import ServiceDetailsModal from '../../../components/vendor/ServiceDetailsModal';
import { useToastContext } from "../../../contexts/ToastContext";
import { 
  useAdminVendorServices, 
  useAdminVendorServiceStatistics,
  useToggleVendorServiceStatus,
  useToggleVendorServiceFeatured,
  useDeleteVendorService
} from '../../../services/adminVendorServiceService';
import { useAdminVendors } from '../../../hooks/useAdminVendors';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';

const AdminVendorServices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { success, error: showError } = useToastContext();

  // Query parameters - no pagination since DataTable handles it
  const queryParams = {
    search: searchTerm || undefined,
    is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    is_featured: featuredFilter === 'all' ? undefined : featuredFilter === 'featured',
    service_category_id: categoryFilter === 'all' ? undefined : categoryFilter,
    vendor_id: vendorFilter === 'all' ? undefined : vendorFilter,
    sort_by: 'created_at',
    sort_order: 'desc'
  };

  const { data: vendorsResponse } = useAdminVendors({ per_page: 500, sort_by: 'created_at', sort_order: 'desc' });

  const vendorOptions = useMemo(() => {
    const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
    if (!Array.isArray(vendors)) return [{ value: 'all', label: 'All Vendors' }];

    const options = vendors
      .filter((v) => v && (v.vendor_profile || v.vendorProfile))
      .map((vendor) => {
        const vp = vendor.vendor_profile || vendor.vendorProfile;
        const name = vp?.business_name || vendor.name || 'Vendor';
        const city = vp?.city || '';
        const state = vp?.state || '';
        const loc = [city, state].filter(Boolean).join(', ');
        return {
          value: vp.id,
          label: loc ? `${name} — ${loc}` : name,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: 'all', label: 'All Vendors' }, ...options];
  }, [vendorsResponse]);

  // Fetch data
  const { 
    data: servicesData, 
    isLoading: loading, 
    error: servicesError,
    refetch 
  } = useAdminVendorServices(queryParams);

  const { 
    data: statisticsData, 
    isLoading: statsLoading 
  } = useAdminVendorServiceStatistics();

  // Extract data
  const services = servicesData?.data || [];
  const statistics = statisticsData?.data;

  // Mutations
  const toggleStatusMutation = useToggleVendorServiceStatus({
    onSuccess: () => {
      success('Service status updated successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to update service status');
    }
  });

  const toggleFeaturedMutation = useToggleVendorServiceFeatured({
    onSuccess: () => {
      success('Service featured status updated successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to update featured status');
    }
  });

  const deleteServiceMutation = useDeleteVendorService({
    onSuccess: () => {
      success('Service deleted successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to delete service');
    }
  });

  const isStatusToggling = (serviceId) =>
    toggleStatusMutation.isPending && toggleStatusMutation.variables === serviceId;

  const isFeaturedToggling = (serviceId) =>
    toggleFeaturedMutation.isPending && toggleFeaturedMutation.variables === serviceId;

  const handleToggleStatus = (serviceId) => {
    toggleStatusMutation.mutate(serviceId);
  };

  const handleToggleFeatured = (serviceId) => {
    toggleFeaturedMutation.mutate(serviceId);
  };

  const handleDeleteService = (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      deleteServiceMutation.mutate(serviceId);
    }
  };

  const handleRowClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  // Define columns for DataTable (reduced for better UX)
  const columns = [
    {
      key: 'service_name',
      label: 'Service',
      sortable: true,
      searchable: true,
      render: (service) => (
        <div className="flex items-center cursor-pointer" onClick={() => handleRowClick(service)}>
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center">
              <Package className="h-5 w-5 text-white dark:text-gray-900" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              {service.service_name}
              
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {service.description}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: false,
      searchable: true,
      render: (service) => (
        <div className="flex items-center cursor-pointer" onClick={() => handleRowClick(service)}>
          <div className="flex-shrink-0 h-8 w-8">
            <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
              <User className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </div>
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {service.vendor_profile?.user?.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {service.service_category?.category_name || 'Unknown'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'base_price',
      label: 'Price',
      sortable: true,
      render: (service) => (
        <div className="flex items-center cursor-pointer" onClick={() => handleRowClick(service)}>
          <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-50">
            ${parseFloat(service.base_price).toFixed(2)}
          </span>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (service) => (
        <PillToggle
          checked={Boolean(service.is_active)}
          onChange={() => handleToggleStatus(service.id)}
          loading={isStatusToggling(service.id)}
          variant="success"
          onLabel="On"
          offLabel="Off"
          ariaLabel={`${service.service_name} active status`}
        />
      ),
    },
    {
      key: 'is_featured',
      label: 'Featured',
      sortable: true,
      render: (service) => (
        <PillToggle
          checked={Boolean(service.is_featured)}
          onChange={() => handleToggleFeatured(service.id)}
          loading={isFeaturedToggling(service.id)}
          variant="amber"
          onLabel="Yes"
          offLabel="No"
          ariaLabel={`${service.service_name} featured status`}
        />
      ),
    },
  ];

  const actions = [
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (service) => handleDeleteService(service.id),
      title: 'Delete Service',
      variant: 'danger',
      size: 'sm',
    },
  ];

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'category':
        setCategoryFilter(value);
        break;
      case 'featured':
        setFeaturedFilter(value);
        break;
      case 'vendor':
        setVendorFilter(value);
        break;
      default:
        break;
    }
  };

  if (servicesError) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Services</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-6">
            {servicesError.message || 'Failed to load vendor services'}
          </p>
          <Button className="!rounded-xl" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Vendor Services"
        subtitle="Manage all vendor services across the platform."
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.services.title}
        steps={ADMIN_PAGE_GUIDES.services.steps}
        icon={ListChecks}
      />

      {statistics && !statsLoading && (
        <StatsGrid
          stats={[
            {
              key: 'total',
              title: 'Total Services',
              value: statistics.total_services,
              icon: Package,
              variant: 'default'
            },
            {
              key: 'active',
              title: 'Active Services',
              value: statistics.active_services,
              icon: CheckCircle,
              variant: 'success'
            },
            {
              key: 'inactive',
              title: 'Inactive Services',
              value: statistics.inactive_services,
              icon: XCircle,
              variant: 'danger'
            },
            {
              key: 'featured',
              title: 'Featured Services',
              value: statistics.featured_services,
              icon: Star,
              variant: 'warning'
            }
          ]}
          columns={4}
        />
      )}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SearchSelect
                label="Vendor"
                value={vendorFilter}
                onChange={(value) => handleFilterChange('vendor', value)}
                options={vendorOptions}
                placeholder="All vendors"
                searchable
                size="md"
              />
              <SearchSelect
                label="Status"
                value={statusFilter}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Select status"
                searchable={false}
                size="md"
              />
              <SearchSelect
                label="Featured"
                value={featuredFilter}
                onChange={(value) => handleFilterChange('featured', value)}
                options={[
                  { value: 'all', label: 'All Services' },
                  { value: 'featured', label: 'Featured Only' },
                  { value: 'regular', label: 'Regular Only' }
                ]}
                placeholder="Select featured status"
                searchable={false}
                size="md"
              />
              <SearchSelect
                label="Category"
                value={categoryFilter}
                onChange={(value) => handleFilterChange('category', value)}
                options={[
                  { value: 'all', label: 'All Categories' }
                ]}
                placeholder="Select category"
                searchable={false}
                size="md"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading vendor services…</p>
            </div>
          ) : (
            <DataTable
              data={services}
              columns={columns}
              actions={actions}
              loading={false}
              searchable={true}
              filterable={false}
              sortable={true}
              selectable={false}
              pageSize={15}
              showPagination={true}
              searchPlaceholder="Search services..."
              emptyMessage="No vendor services found."
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>

      <ServiceDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        service={selectedService}
      />
    </div>
  );
};

export default AdminVendorServices;
